import { Controller } from "@hotwired/stimulus"
import { setOptions, importLibrary } from "@googlemaps/js-api-loader"

/**
 * Google Address Autocomplete Controller
 *
 * This controller integrates Google Maps Places API autocomplete functionality
 * for address input. It handles:
 * - Loading and initializing the Google Places API
 * - Creating a PlaceAutocompleteElement with custom styling
 * - Monkey-patching Shadow DOM to enable custom styling (closed -> open)
 * - Parsing place data and populating address form fields
 *
 * @example
 *   <div data-controller="google-address">
 *     <div data-google-address-target="searchMount"></div>
 *     <div>
 *       <input data-google-address-target="street" />
 *       <!-- ... other address fields -->
 *     </div>
 *   </div>
 */
export default class extends Controller {
    static targets = [
        "searchMount",
        "street", "line2", "suburb", "city", "province", "postalCode"
    ]

    /**
     * Lifecycle: Initialize the controller
     * - Loads Google Maps Places API
     * - Monkey-patches Shadow DOM for custom styling
     * - Initializes the autocomplete element
     */
    async connect() {
        const apiKey = "AIzaSyDxb-rnHA2EY7C1wY6eX45sfFmnzctmtvE"

        setOptions({ key: apiKey, version: "weekly" })
        await importLibrary("places")

        // Patch Shadow DOM before creating element to enable custom styling
        this.patchShadowDOM()
        this.initPlaceElement()
    }

    /**
     * Lifecycle: Cleanup when controller disconnects
     * Restores original attachShadow method to avoid side effects
     */
    disconnect() {
        if (this.originalAttachShadow) {
            Element.prototype.attachShadow = this.originalAttachShadow
            this.originalAttachShadow = null
        }
    }

    /**
     * Monkey-patches Element.prototype.attachShadow to force Shadow DOM mode to 'open'
     * for Google Maps PlaceAutocompleteElement, enabling custom styling of internal elements.
     *
     * This is necessary because Google's component uses closed Shadow DOM by default,
     * which prevents CSS from targeting internal elements like the focus ring and input.
     *
     * @private
     */
    patchShadowDOM() {
        // Only patch once to avoid multiple patches
        if (this.originalAttachShadow) return

        // Store original method for cleanup
        this.originalAttachShadow = Element.prototype.attachShadow
        const self = this

        Element.prototype.attachShadow = function(options) {
            // Force 'open' mode only for Google Maps autocomplete element
            if (this.tagName === 'GMP-PLACE-AUTOCOMPLETE') {
                options = { ...options, mode: 'open' }
            }

            const shadowRoot = self.originalAttachShadow.call(this, options)

            // Apply custom styles after Shadow DOM is attached
            if (this.tagName === 'GMP-PLACE-AUTOCOMPLETE') {
                // Delay to ensure shadow DOM is fully rendered
                setTimeout(() => self.styleShadowDOM(shadowRoot), 100)
            }

            return shadowRoot
        }
    }

    /**
     * Applies custom styles to Shadow DOM elements
     * - Hides the focus ring overlay
     * - Sets input text color to inherit from parent
     * - Styles the clear button for elegant hover/focus states
     * - Injects persistent style tag into shadow root
     *
     * @param {ShadowRoot} shadowRoot - The shadow root to style
     * @private
     */
    styleShadowDOM(shadowRoot) {
        if (!shadowRoot) return

        // Hide the focus ring div that overlays the input
        const focusRing = shadowRoot.querySelector('.focus-ring')
        if (focusRing) {
            focusRing.style.opacity = '0'
            focusRing.style.display = 'none'
        }

        // Make input text color inherit from parent
        const input = shadowRoot.querySelector('input')
        if (input) {
            input.style.color = 'inherit'
        }

        // Style the clear button
        const clearButton = shadowRoot.querySelector('.clear-button')
        if (clearButton) {
            clearButton.style.transition = 'all 0.2s ease-in-out'
        }

        // Inject persistent styles into shadow root
        const style = document.createElement('style')
        style.textContent = `
      /* Hide focus ring */
      .focus-ring {
        opacity: 0 !important;
        display: none !important;
      }
      
      /* Input text color */
      input {
        color: inherit !important;
      }
      
      /* Clear button elegant styling with theme colors */
      .clear-button {
        height: 33px;
        width: 33px;
        bottom: 2px;
        position: relative;
        background: transparent !important;
        border: none !important;
        padding: 8px !important;
        border-radius: 50% !important;
        transition: all 0.2s ease-in-out !important;
        opacity: 0.5 !important;
        color: oklch(0.6797 0 0) !important; /* dark-grey from your theme */
      }
      
      .clear-button:hover {
        opacity: 1 !important;
        background: oklch(0.9791 0 0 / 0.5) !important; /* grey-custom with transparency */
        transform: scale(1.05) !important;
        color: oklch(0.1822 0 0) !important; /* dark-custom from your theme */
      }
      
      .clear-button:active {
        transform: scale(0.95) !important;
        background: oklch(0.9791 0 0) !important; /* grey-custom */
      }
      
      .clear-button:focus {
        outline: none !important;
        box-shadow: 0 0 0 2px oklch(0.9791 0 0) !important; /* grey-custom */
      }
      
      /* Clear button SVG styling */
      .clear-button svg {
        display: block !important;
        width: 18px !important;
        height: 18px !important;
      }
      
      .clear-button svg path {
        fill: currentColor !important;
      }
    `
        shadowRoot.appendChild(style)
    }

    /**
     * Initializes the Google Maps PlaceAutocompleteElement
     * - Creates element with South Africa region restriction
     * - Applies Tailwind CSS styling
     * - Mounts element to DOM
     * - Attaches place selection event listener
     *
     * @private
     */
    initPlaceElement() {
        const el = new google.maps.places.PlaceAutocompleteElement({
            includedRegionCodes: ["ZA"], // Restrict to South Africa
            // types: ["address"], // Optional: restrict to addresses only
        })

        // Apply Tailwind classes for consistent styling
        el.className = "md-input-styling border border-dark-custom h-[45px] !rounded-none w-full [&>input]:focus:ring-transparent"
        el.placeholder = "Search address"

        // Mount element
        this.searchMountTarget.innerHTML = ""
        this.searchMountTarget.appendChild(el)

        // Handle place selection
        el.addEventListener("gmp-select", async (evt) => {
            const place = evt.placePrediction.toPlace()
            await place.fetchFields({
                fields: ["addressComponents", "formattedAddress", "location"]
            })
            this.fillFromPlace(place)
        })
    }

    /**
     * Parses Google Place object and populates form fields
     * Extracts address components and fills the editable form fields
     *
     * Address components mapping:
     * - street_number + route = street address (line1)
     * - premise/subpremise = building/complex (line2)
     * - sublocality = suburb
     * - locality = city
     * - administrative_area_level_1 = province
     * - postal_code = postal code
     *
     * @param {google.maps.places.Place} place - The selected place object
     * @private
     */
    fillFromPlace(place) {
        let streetNumber = ""
        let route = ""
        let line2 = ""
        let suburb = ""
        let city = ""
        let province = ""
        let postalCode = ""

        // Parse address components
        for (const component of place.addressComponents || []) {
            const types = component.types || []
            const value = component.longText ?? component.long_name

            if (types.includes("street_number")) streetNumber = value
            if (types.includes("route")) route = value
            if (types.includes("sublocality") || types.includes("sublocality_level_1")) suburb = value
            if (types.includes("locality") || types.includes("postal_town")) city = value
            if (types.includes("administrative_area_level_1")) province = value
            if (types.includes("postal_code")) postalCode = value

            // Building/complex information
            if (types.includes("premise") || types.includes("subpremise")) {
                line2 = line2 ? `${line2} ${value}` : value
            }
        }

        // Combine street number and route
        const street = [streetNumber, route].filter(Boolean).join(" ").trim()

        // Populate the editable fields submitted with the form
        this.streetTarget.value = street
        this.line2Target.value = line2
        this.suburbTarget.value = suburb
        this.cityTarget.value = city
        this.provinceTarget.value = province
        this.postalCodeTarget.value = postalCode
    }
}
